import * as dte from "../DtEventTypes";
import {DtEventUtils} from "../../lmv-compat/DtEventUtils";
import GRADIENT_THEMES from "../resources/gradients.json";
import { ConnectivityUtils } from "../connectivity/ConnectivityUtils";
import {DT_TELEMETRY_CHANGED_EVENT, DT_TELEMETRY_INSTANT_READINGS_CHANGED_EVENT} from "../DtEventTypes";
import debounce from "./timed-debounce";

import {THREE} from "@adsk/lmv";

const _tmpBox = new THREE.Box3();

/**
 * Returns all rooms the stream is either assigned to directly, or rooms assigned to its host element.
 * @param facility
 * @param defaultModel
 * @param stream
 * @return {{dbId, model}[]}
 */
function getRoomsForStream(facility, defaultModel, stream) {
	// Direct room assignment
	let rooms = facility.getRoomsOfElement(defaultModel, stream.dbId);
	if (rooms.length) return rooms;

	// Room assignment from host.
	const {model, hostId} = stream.hostElement;

	// If host is a room
	const hostAsRoom = model.getRooms()[hostId];
	if (hostAsRoom) {
		return [hostAsRoom];
	}

	// If host is in a room
	rooms = facility.getRoomsOfElement(model, hostId);
	if (rooms.length) return rooms;

	return [];
}

/**
 * Returns all elements part of the same sub-system as the stream host element.
 * @param facility
 * @param stream
 * @return {Promise<IterableIterator<{dbId, model}>>}
 */
async function getSystemElementsForStream(facility, stream) {
	const uniqueElements = new Map();
	const {model: hostModel, hostId} = stream.hostElement;
	const systems = await facility.systemsManager.getSystemsOf(hostModel, hostId);
	for (const system of systems) {
		if (!system.hasCachedConnections()) {
			await facility.systemsManager.getConnections(system.id);
		}

		// Get host element as a "SystemElement" that's part of the current system.
		const systemEl = system.getElement(hostModel.urn(), hostId);
		if (!systemEl) {
			continue;
		}

		// Inclusive set of transitively connected elements
		const subSystem = ConnectivityUtils.getConnectedElements(systemEl, true);
		for (const element of subSystem) {
			uniqueElements.set(element.model.urn() + element.dbId, element);
		}
	}

	return uniqueElements.values();
}

async function loadHostElements(hostElements) {
	const byModel = Map.groupBy(hostElements, ({ model }) => model);

	return Promise.all(byModel.entries()
		.map(([model, hostElements]) => model.loader && model.loadElements(hostElements.map(he => he.hostId))));
}

export const SENSED_ELEM_TYPE = Object.freeze({
	ROOM: 'room',
	SYSTEM: 'system'
});

/**
 * Represents a set of sensors and sensed elements.
 *
 * There are two named perspective, room and system based. As sensor readings for rooms describes how occupied spaces
 * in a facility are behaving, while system present an overview of how the mechanisms of a facility functions.
 */
class SensedSpace {
	/** @private */
	constructor(elements = [], positionByStreamId = new Map()) {
		this.elements = elements;
		this.positionByStreamId = positionByStreamId;
	}

	/**
	 * A sensor position in 3D space.
	 * @param {int} dbId Stream's dbId
	 * @return {THREE.Vector3}
	 */
	getSensorPos(dbId) {
		return this.positionByStreamId.get(dbId);
	}

	/**
	 * Returns sensed elements of type ROOM or SYSTEM.
	 * @return {{dbId: int, model: any, streamDbIds: int[], type: SENSED_ELEM_TYPE}[]}
	 */
	allElements() {
		return this.elements;
	}

	/**
	 * Gets the current rooms and system elements associated with streams that have the required parameter.
	 *
	 * @param facility
	 * @param defaultModel
	 * @param streams Stream elements
	 * @param {string} attrId Stream attribute ID (parameter)
	 * @param {string} typeFilter If given, will limit elements to this type.
	 * @return {Promise<SensedSpace>}
	 */
	static async fromStreams(facility, defaultModel, streams, attrId, typeFilter) {

		// load host elements participating in the heatmap visualization.
		await loadHostElements(streams.map((s) => s.hostElement));

		const positionByStreamId = new Map();
		const uniqueElements = new Map();

		const addElement = (dbId, model, stream, type, isVisible) => {
			const key = model.urn() + dbId;
			let stored = uniqueElements.get(key) ?? {dbId, model, streamDbIds: [], type, isVisible}
			stored.streamDbIds.push(stream.dbId);
			uniqueElements.set(key, stored);
		};

		for (const stream of streams) {
			const {model: hostModel, hostId} = stream.hostElement;
			const hostVisible = hostModel.visibilityManager?.isNodeVisible(hostId);
			hostModel.getElementBounds(hostId, _tmpBox);
			// As we load elements based on visibility, some host elements might be unloaded while their heatmap
			// would be visible. This means we do not have a stream position.
			const position = _tmpBox.isEmpty()
				? (hostVisible ? null : undefined) // null = not yet loaded, undefined = likely won't load
				: _tmpBox.getCenter();
			positionByStreamId.set(stream.dbId, position);

			if (!typeFilter || typeFilter === SENSED_ELEM_TYPE.ROOM) {
				const rooms = getRoomsForStream(facility, defaultModel, stream);
				for (const {dbId, model} of rooms) {
					const isVisible = facility.isModelVisible(model) && model.visibilityManager?.isNodeVisible(dbId);
					addElement(dbId, model, stream, SENSED_ELEM_TYPE.ROOM, isVisible);
				}
			}

			if (!typeFilter || typeFilter === SENSED_ELEM_TYPE.SYSTEM) {
				const systems = await getSystemElementsForStream(facility, stream);
				for (const {dbId, model} of systems) {
					const isVisible = facility.isModelVisible(model) && model.visibilityManager?.isNodeVisible(dbId);
					addElement(dbId, model, stream, SENSED_ELEM_TYPE.SYSTEM, isVisible);
				}
			}
		}

		return new SensedSpace([...uniqueElements.values()], positionByStreamId);
	}
}

/** Presents a volumetric heatmap around elements "sensed" by streams. */
export class StreamHeatmap {

	constructor(streamManager, eventSource, dataSource) {
		this.streamManager = streamManager;
		this.eventSource = eventSource;
		this.materials = new HeatmapMaterials();

		// Connected to the correct source of telemetry events.
		this.dataSource = dataSource ?? new TelemetryStreamSource(streamManager, eventSource);

		this.attrIdFilter = null;

		this.verbose = false;
		//this.eventSource.debugEvents(true);
	}

	init(viewer) {
		this.viewer = viewer;
	}

	dispose() {
		this.reset();
	}

	/** Get color gradient stops from the original shading element */
	getColorStops() {
		return this.stops.map((stop, i) => ({
			color: this.colors[i].getStyle(),
			offset: stop,
			opacity: this.opacity,
		}));
	}

	/**
	 * Finds elements "sensed" by a stream with this parameter and colors the elements based on streams' latest values.
	 *
	 * @param {string|null} attrId Stream attribute ID (parameter)
	 * @param {boolean} forceReload Force reload the heatmap.
	 * @param {HeatmapPref} preferences User preference for the heatmap configuration.
	 * @param {function(SensedSpace):boolean} checkValidity Returns if the heatmap should draw.
	 * @return {Promise<void>}
	 */
	async show(attrId = null, forceReload = false, preferences = {}, checkValidity = () => true) {
		if (attrId === this.attrIdFilter && !forceReload) {
			return;
		}

		if (!attrId) {
			// Turns heatmap off.
			this.verbose && console.info('Heatmap turns off');
			this.reset();
			this.viewer.impl.invalidate(true);
			return;
		}

		// Resetting shading before creating new
		this.reset(true);

		let streams = await this.streamManager.getAllStreamInfos();
		streams = streams.filter((s) => s.hostElement && s.streamAttrs.some(attr => attr.id === attrId));

		const { defaultModel, facility } = this.streamManager;
		const { appliesTo, isReversed, opacity, range: userRange, theme } = preferences;
		const sensedSpc = await SensedSpace.fromStreams(facility, defaultModel, streams, attrId, appliesTo);

		this.verbose && console.info('Heatmap turns on for', attrId, 'forced', forceReload);
		this.attrIdFilter = attrId;
		this.setColorStops(theme, opacity, isReversed);

		const heatmapStops = new Float32Array(this.stops);
		const heatmapColors = new Float32Array(3 * this.colors.length);
		this.colors.forEach((color, i) => color.toArray(heatmapColors, i * 3));

		const onData = (id2reading, range) => {
			if (!rangeEquals(this.range, range)) {
				this.range = range;
				this.eventSource.dispatchEvent({ type: dte.DT_HEATMAP_CHANGED_EVENT, change: { attrIdFilter: attrId, ctype: 'range' } });
			}

			this.verbose && console.time('StreamHeatmap<render>');
			if (checkValidity(sensedSpc, preferences, id2reading)) {
				this.render(sensedSpc, (dbId) => id2reading.get(dbId), heatmapStops, heatmapColors, this.opacity);
			} else {
				this.render(sensedSpc, null);
			}
			this.verbose && console.timeEnd('StreamHeatmap<render>');
		}

		if (this.viewer.impl.isWebGPU()) {
			// This is the WebGPU case
			this.viewer.impl.glrenderer().getIBL().setHeatmaps(heatmapColors, heatmapStops, this.opacity);
		}
		await this.dataSource.start(streams, attrId, userRange, onData);

		this.eventSource.dispatchEvent({
			type: dte.DT_HEATMAP_CHANGED_EVENT,
			change: { attrIdFilter: attrId, ctype: 'shading' }
		});
	}

	/**
	 * Renders updates the heatmap color assuming nothing changes but telemetry data.
	 * Any other meaningful changes should trigger a new show().
	 * @private
	 */
	render(sensedSpace, sensorValueCallback, heatmapStops, heatmapColors, alpha) {
		const viewerImpl = this.viewer.impl;

		let shadingValues, heatmapSensors, prevOffset = 0, curOffset = 0;
		if (viewerImpl.isWebGPU()) {
			shadingValues = [];
		}

		for (const {dbId, model, streamDbIds, isVisible} of sensedSpace.allElements()) {
			// isVisible should not change during a series of render, no need to update sensor value or create material.
			if (!isVisible) continue;

			if (!viewerImpl.isWebGPU()) {
				// WebGL could also be converted to the offset/count from WebGPU.
				shadingValues = [];
			}
			if (sensorValueCallback) {
				for (const streamDbId of streamDbIds) {
					const value = sensorValueCallback(streamDbId);
					if (!Number.isFinite(value)) continue;

					const pos = sensedSpace.getSensorPos(streamDbId);
					if (pos) {
						// Can be optimized since all elements are usually sharing a smaller set of sensors.
						shadingValues.push(pos.x, pos.y, pos.z, value);
						curOffset++;
					}
				}
			}
			if (!viewerImpl.isWebGPU()) {
				heatmapSensors = new Float32Array(shadingValues);
			}

			const updateMaterialData = (material) => {
				if (!viewerImpl.isWebGPU()) {
					material.heatmapSensors = heatmapSensors;
					material.heatmapAlpha = alpha;
					material.heatmapColors = heatmapColors;
					material.heatmapStops = heatmapStops;
				} else {
					material.heatmapSensorOffset = prevOffset;
					material.heatmapSensorCount = curOffset - prevOffset;
				}
				material.needsUpdate = true;
			}

			let material = this.materials.get(model, dbId);
			if (material) {
				updateMaterialData(material);
			} else {
				// First time, we need to make it and cache it.
				const fl = model.getFragmentList();
				const it = model.getData().instanceTree;
				it.enumNodeFragments(dbId, (fragId) => {

					const mat = fl.getMaterial(fragId);
					// Only allow for main working materials.
					if (!mat || !(mat instanceof THREE.MeshPhongMaterial || mat instanceof THREE.MeshBasicMaterial)) {
						return;
					}

					if (!material) {
						material = viewerImpl.matman().cloneMaterial(mat, model);
						if(mat.isRoomMaterial) {
							// bump opacity at least above alpha cut off. otherwise the frag
							// shader might discard before even getting to the heatmap code.
							material.opacity = Math.max(material.opacity, 0.012);
							material.isRoomMaterial = true;
						}
						material.depthWrite = mat.depthWrite || alpha === 1;

						material.polygonOffset = true;
						material.polygonOffsetFactor = 0.5;
						material.polygonOffsetUnits = 1;

						updateMaterialData(material);
					}

					this.materials.addRevert(fl, fragId, mat);
					fl.setMaterial(fragId, material);
				}, true);

				// We could be more precise and mark the incompatible elements to not be retried.
				if (material) this.materials.set(model, dbId, material);
			}

			prevOffset = curOffset;
		}

		if (viewerImpl.isWebGPU() && curOffset > 0) {
			viewerImpl.glrenderer().getIBL().setHeatmapSensors(shadingValues);
		}

		this.viewer.impl.invalidate(true);
	}

	/** @private */
	reset(preventDispatch = false) {
		this.materials.revert(this.viewer.impl.isWebGPU());
		this.dataSource.reset();
		this.attrIdFilter = null;

		if (!preventDispatch) {
			this.eventSource.dispatchEvent({
				type: dte.DT_HEATMAP_CHANGED_EVENT,
				change: { attrIdFilter: null, ctype: 'shading' }
			});
		}
	}

	/** @private */
	setColorStops(theme, opacity, isReversed) {
		/** Returns n equidistant points along the [0, 1] line with n > 0. */
		function createStops(n) {
			// ex: {n:2} => [0/1, 1/1], {n:3} => [0/2, 1/2, 2/2]
			const res = [];
			const inc = 1 / (n - 1);
			for (let i = 0; i < n; i++) {
				res.push(inc * i);
			}
			return res;
		}

		const colors = (GRADIENT_THEMES.find(t => t.id === theme) || GRADIENT_THEMES[0]).colors;
		this.stops = createStops(colors.length);
		this.opacity = opacity ?? 0.9;
		this.colors = (isReversed ? [...colors].reverse() : colors).map((c) => new THREE.Color(c))
	}
}

const key = (model, dbId) => `${model.id}|${dbId}`;
class HeatmapMaterials {
	reverts = [];
	mats = new Map();

	addRevert(fl, fragId, orgMaterial) {
		this.reverts.push([fl, fragId, orgMaterial]);
	}

	get(model, dbId) {
		return this.mats.get(key(model, dbId));
	}

	set(model, dbId, cloned) {
		this.mats.set(key(model, dbId), cloned);
	}

	revert(isWebGPU) {
		for (const cloned of this.mats.values()) {
			// Make sure to not leak any array buffers.
			if (!isWebGPU) {
				cloned.heatmapSensors = undefined;
				cloned.heatmapStops = undefined
				cloned.heatmapColors = undefined;
			}

			// Also invoke registered dispose handlers, e.g. to clean up GPU resources.
			cloned.dispose();
		}
		this.mats.clear();

		// Revert fragments to their previous materials.
		for (const [fl, fragId, orgMaterial] of this.reverts) {
			fl.materialmap && fl.setMaterial(fragId, orgMaterial);
		}
		this.reverts.length = 0;
	}
}

const EMPTY_RANGE = { min: Infinity, max: -Infinity };
class TelemetryStreamSource {
	constructor(streamManager, eventSource, fps = 10) {
		this.streamManager = streamManager;
		this.throttleMs = 1_000 / fps;
	}

	async start(streams, attrId, userRange, onData) {
		const refreshReadings = async () => {
			const readings = await this.getScaledReadings(streams, attrId, userRange, this.streamManager.view);
			if (readings) {
				onData(readings.id2reading, readings.range);
				return true;
			} else {
				console.info('heatmap readings are unavailable');
				return false;
			}
		};
		this.throttledUpdate = debounce(refreshReadings, this.throttleMs, { leading: true, maxWait: this.throttleMs, trailing: true });
		this.streamManager.view.addEventListener(DT_TELEMETRY_INSTANT_READINGS_CHANGED_EVENT, this.throttledUpdate);

		const refreshed = await refreshReadings();
		if (!refreshed) {
			// Wait until the telemetry has finished loading.
			await DtEventUtils.waitForFilteredEvent(this.streamManager.view, DT_TELEMETRY_CHANGED_EVENT, null);
			await this.throttledUpdate();
		}
	}

	reset() {
		if (!this.throttledUpdate) return;

		this.streamManager.view.removeEventListener(DT_TELEMETRY_INSTANT_READINGS_CHANGED_EVENT, this.throttledUpdate);
		this.throttledUpdate = null;
	}

	/** @private */
	async getScaledReadings(streams, attrId, userRange, telemetryView) {
		const rawReadings = await telemetryView.getInstantReadings();
		if (!rawReadings) return;

		const dataRng = await telemetryView.getDataRange(attrId) ?? EMPTY_RANGE;
		const range = adjustRange(dataRng, userRange);

		const space = range.max - range.min;
		const scale = (val) => space ? (val - range.min) / space : 0.5;

		const id2reading = new Map();
		for (const stream of streams) {
			const reading = rawReadings?.get(stream.dbId)?.[attrId];
			if (!reading) continue;

			const [_, value] = reading;
			id2reading.set(stream.dbId, clamp(scale(value), 0, 1));
		}

		return { id2reading, range };
	}
}

function clamp(value, min, max) {
	return Math.max(Math.min(value, max), min);
}

function rangeEquals(lhs, rhs) {
	return lhs?.min === rhs?.min && lhs?.max === rhs?.max;
}

function adjustRange(dataRng, userRng) {
	let { min, max } = dataRng;

	// if the suggested min/max are out of the range given by user, "stretch" the observed range accordingly to fit the users input
	const clampUserRange = (min, max, umin, umax) => {
		const hasUserMin = Number.isFinite(umin), hasUserMax = Number.isFinite(umax);
		if (hasUserMin && hasUserMax)
			return [umin, umax]
		if (hasUserMin)
			return [umin, Math.max(umin, max)]
		if (hasUserMax)
			return [Math.min(min, umax), umax]
		return [min, max]
	}

	[min, max] = clampUserRange(min, max, userRng?.min, userRng?.max);

	// If all readings are the same, we artificially pad the min and max
	// this has edge case implication like going out of logical range.
	if (min === max) {
		min -= 1;
		max += 1;
	}

	return { min, max };
}
